/**
 * Internal dependencies
 */
import { onScrollDown } from '../utils';
import { domReady } from '../../utils/helpers';
import { throttle } from '../../utils/lodash';
import { addQueryArgs } from '../../utils/url';
import type { ConvertibleAction, Convert } from '../../types';

import type { Attributes } from '../../../../../packages/conversion-action-library/video-playback/types';

const YOUTUBE_PLAY = 1;
const YOUTUBE_END = 0;

export function listenToVideoPlaybackEvent(
	allActions: ReadonlyArray< ConvertibleAction >,
	convert: Convert
): void {
	const actions = allActions.filter( isVideoPlaybackAction );
	if ( ! actions.length ) {
		return;
	}

	actions.forEach( ( action ) => {
		switch ( action?.attributes?.platform ) {
			case 'youtube':
				return listenToYouTubeVideoPlaybackEvent( action, convert );
		}
	} );
}

// =======
// HELPERS
// =======

function isVideoPlaybackAction(
	action: ConvertibleAction
): action is ConvertibleAction< Attributes > {
	return 'nab/video-playback' === action.type;
}

function listenToYouTubeVideoPlaybackEvent(
	action: ConvertibleAction< Attributes >,
	convert: Convert
): void {
	const apiReady = loadYouTubeApi();
	apiReady( () => {
		addYouTubeListeners( action, convert );
		onScrollDown(
			throttle( () => addYouTubeListeners( action, convert ), 1000 )
		);
	} );
}

let youTubeApiStatus: 'unavailable' | 'loading' | 'ready' = 'unavailable';
function loadYouTubeApi() {
	domReady( () => {
		if ( 'unavailable' !== youTubeApiStatus ) {
			return;
		}
		youTubeApiStatus = 'loading';

		/* eslint-disable */
		const fn: () => void =
			( window as any ).onYouTubeIframeAPIReady ?? ( () => {} );
		( window as any ).onYouTubeIframeAPIReady = () => {
			/* eslint-enable */
			youTubeApiStatus = 'ready';
			fn();
		};

		const tag = document.createElement( 'script' );
		tag.src = 'https://www.youtube.com/iframe_api';
		document.head.appendChild( tag );
	} );

	return ( fn: () => void ) => {
		const check = ( attempt: number ) => {
			if ( 'ready' === youTubeApiStatus ) {
				return fn();
			}
			if ( 5 <= attempt ) {
				return;
			}
			setTimeout( () => check( attempt + 1 ), attempt * 2000 );
		};
		check( 1 );
	};
}

const listenedYouTubeIframes: HTMLIFrameElement[] = [];
function addYouTubeListeners(
	action: ConvertibleAction< Attributes >,
	convert: Convert
) {
	const { videoId, event } = action.attributes;

	getYouTubeIframes( videoId )
		.filter( ( iframe ) => ! listenedYouTubeIframes.includes( iframe ) )
		.forEach( ( iframe ) => {
			listenedYouTubeIframes.push( iframe );
			// NOTE. Improve these types.
			/* eslint-disable */
			new ( window as any ).YT.Player( iframe, {
				events: {
					onStateChange: ( ev: { data: number } ) => {
						switch ( ev.data ) {
							case YOUTUBE_PLAY:
								return 'play' === event && convert( action );

							case YOUTUBE_END:
								return 'end' === event && convert( action );
						}
					},
				},
			} );
			/* eslint-enable */
		} );
}

function getYouTubeIframes( videoId: string ) {
	const iframes = Array.from(
		document.querySelectorAll< HTMLIFrameElement >(
			`iframe[src*="${ videoId }"]`
		)
	).filter(
		( iframe ) =>
			iframe.src.includes( 'youtube' ) ||
			iframe.src.includes( 'youtu.be' )
	);

	iframes
		.filter( ( iframe ) => ! iframe.src.includes( 'enablejsapi' ) )
		.forEach( ( iframe ) => {
			iframe.src = addQueryArgs( iframe.src, { enablejsapi: 1 } );
		} );

	return iframes;
}
